<script setup lang="ts">
import { computed, ref } from 'vue'
import { useNow } from '@/composables/game/time'
import CountdownDisplay from '@/components/CountdownDisplay.vue'
import BaseLinkButton from '@/components/base/BaseLinkButton.vue'
import ArrowRightIcon from '@/components/icons/ArrowRightIcon.vue'
import { usePlayer } from '@/composables/game/player'
import CheckIconThin from '@/components/icons/CheckIconThin.vue'
import { useTonConnectModal } from '@townsquarelabs/ui-vue'
import { useWalletConnect } from '@/composables/game/wallet-connect'
import { useSnapshot } from '@/composables/game/snapshot'
import router from '@/router'
import { RouteName } from '@/routes'
import { AchievementCategory, useAchievements, useAchievementsPage } from '@/composables/game/achievements'
import { AchievementType } from '@/types/achievements'
import { formatUnshortenedNumber } from '@/helpers/utils'
import { useCurrentLevelRankFetcher } from '@/composables/game/leader-board'
import { DateTime } from 'luxon'
import FAQBox from '@/components/FAQBox.vue'
import SnapshotInfoModal from '@/components/modals/SnapshotInfoModal.vue'
import CheckCircleThin from '@/components/icons/CheckCircleThin.vue'
import WalletConnect from '@/components/WalletConnect.vue'
import BaseButton from '@/components/base/BaseButton.vue'

const { now } = useNow()
const { player } = usePlayer()
const { open } = useTonConnectModal()
useWalletConnect()
const { achievements, isFetchingAchievements, categoryGroups, categoryTitleMap } = useAchievementsPage(false)
const { getMilestone } = useAchievements()
const { currentRank, effortLeaderboardCurrentRank } = useCurrentLevelRankFetcher()
const { isAfterSnapshot } = useSnapshot()

const snapShotTimestamp = DateTime.fromISO('2025-05-31T09:00:00', { zone: 'utc' }).toSeconds()
const formattedPostSnapshotText = DateTime
  .fromSeconds(snapShotTimestamp, { zone: 'utc' })
  .toFormat('\'Snapshot taken on\' dd LLL \'at\' h.mm a \'UTC\'')

const countdown = computed(() => {
  const timeDiff = snapShotTimestamp - now.value
  return Math.max(0, timeDiff)
})

const minLevel = 10

const playerWalletAddress = computed(() => player.value.settings.tonWalletAddress)
const userReachedMinLevel = computed(() => player.value.level >= minLevel)

const showSnapshotInfoModal = ref(false)

const preSnapshotFaq = [
  {
    question: '1. What is the Dropee airdrop?',
    answer: `
      Our airdrop rewards players for their dedication to Dropee—recognizing activity, engagement, and loyalty with $DROPEE tokens when we launch.
    `,
  },
  {
    question: '2. Which metrics will we track?',
    answer: `
      We'll consider three pillars of your in-game activity:
      <ul class="list-disc ml-5">
        <li class="mt-1"><span class="font-semibold text-white/[.87]">Dropee Journey:</span> Overall time and ranking in the game, loyalty streaks, and community referrals.</li>
        <li class="mt-1"><span class="font-semibold text-white/[.87]">Gameplay &amp; Activity:</span> Cards, quests, spins, combos, and general progression.</li>
        <li class="mt-1"><span class="font-semibold text-white/[.87]">Advanced &amp; Events:</span> Any advanced gameplay and special event achievements.</li>
      </ul>
    `,
  },
  {
    question: '3. How many phases are there?',
    answer: `
      <ul class="list-disc ml-5">
        <li class="mt-1"><span class="font-semibold text-white/[.87]">Phase 1: Snapshot (May 31 @ 09:00 UTC)</span><br>
            We capture your current metrics to establish a baseline score.
        </li>
        <li class="mt-1"><span class="font-semibold text-white/[.87]">Phase 2: Token Generation Event (Timing TBD)</span><br>
            When market conditions align, you'll be able to claim your tokens—and anyone who keeps playing after May 31 may see their standing improve before the final launch.
        </li>
      </ul>
    `,
  },
  {
    question: '4. Can I keep improving after May 31?',
    answer: `
      Yes! While the snapshot locks in your baseline, continued play—leveling cards, completing quests, joining events—can give you an extra edge when allocations are finalized.
    `,
  },
  {
    question: '5. Do I need to link a wallet?',
    answer: `
      You will need a compatible wallet to claim your tokens once the TGE goes live. Details on wallet setup will be shared before Phase 2.
    `,
  },
  {
    question: '6. When can I actually claim my tokens?',
    answer: `
      We'll announce the exact claim date as soon as market conditions are right. Keep an eye on official channels for updates.
    `,
  },
  {
    question: '7. What if I have questions or run into issues?',
    answer: `
      Drop us a message via <span class="font-semibold text-white/[.87]">@DropeeSupportBot</span> on Telegram. Our team will assist you within 48 hours.
    `,
  },
]

const postSnapshotFaq = [
  {
    question: '1. What happens now that the snapshot is done?',
    answer: `
      <p>The snapshot has captured your final Phase 2 stats on <span class="font-semibold text-white/[.87]">December 19th, 2025</span>.</p>
      <p class="mt-3">We're now calculating everyone's <span class="font-semibold text-white/[.87]">$DROPEE allocation</span>.</p>
      <p class="mt-3"><span class="font-semibold text-white/[.87]">Your allocation progress is locked,</span> but you can keep playing.</p>
      <p class="mt-3">Your allocation will be revealed at the TGE.</p>
    `,
  },
  {
    question: '2. When will the $DROPEE airdrop happen?',
    answer: `
      <p>The airdrop is planned for <span class="font-semibold text-white/[.87]">early 2026</span>.</p>
      <p class="mt-3">All official updates will be shared <span class="font-semibold text-white/[.87]">inside the Dropee App</span> and on <span class="font-semibold text-white/[.87]">verified Dropee socials</span>.</p>
    `,
  },
  {
    question: '3. What will happen to the Dropee app?',
    answer: `
      <p>In a few days, Dropee <span class="font-semibold text-white/[.87]">will gradually transition into Season 2</span>, bringing a <span class="font-semibold text-white/[.87]">refreshed experience and new opportunities</span> for the community.</p>
      <p class="mt-3">All Dropee games remain accessible, and your activity continues to <span class="font-semibold text-white/[.87]">power the ecosystem</span>.</p>
      <p class="mt-3">Each app will progressively introduce new $DROPEE-powered utilities.</p>
    `,
  },
  {
    question: '4. How is my $DROPEE allocation calculated?',
    answer: `
      <p>Your allocation is based on <span class="font-semibold text-white/[.87]">Phase 1 + Phase 2 combined</span>, including:</p>
      <ul class="list-disc pl-5 mt-3 space-y-1">
        <li>Time in-game, ranks, loyalty & contributions</li>
        <li>Core gameplay actions and progression</li>
        <li>Events, achievements, badges, TGE boosts</li>
      </ul>
      <p class="mt-3">Everything you did counts.</p>
    `,
  },
  {
    question: '5. How will claiming work?',
    answer: `
      <p>Claiming will happen on a <span class="font-semibold text-white/[.87]">dedicated $DROPEE Token App</span>.</p>
      <p class="mt-3">You'll be able to:</p>
      <ul class="list-disc pl-5 mt-2 space-y-1">
        <li>View your eligibility & final allocation</li>
        <li>Claim your tokens</li>
        <li>Stake $DROPEE to unlock rewards</li>
      </ul>
      <p class="mt-3"><span class="text-yellow-400">⚠️</span> <span class="font-semibold text-white/[.87]">Only use the official link shared in-app or on verified channels.</span></p>
    `,
  },
  {
    question: '6. Can I claim with a different wallet?',
    answer: `
      <p>No. You must use the <span class="font-semibold text-white/[.87]">same TON wallet linked to your Dropee profile</span>.</p>
    `,
  },
  {
    question: '7. What happens after the TGE?',
    answer: `
      <p>The TGE is just the beginning.</p>
      <p class="mt-3">After launch, you'll be able to:</p>
      <ul class="list-disc pl-5 mt-2 space-y-1">
        <li>Earn more $DROPEE by playing across Dropee games</li>
        <li>Unlock exclusive drops, perks, and access</li>
        <li>Stake tokens to earn rewards</li>
        <li>Shape future token-powered features</li>
      </ul>
      <p class="mt-3"><span class="font-semibold text-white/[.87]">The future of Dropee is built with you — and your tokens are your entry ticket.</span></p>
    `,
  },
  {
    question: '8. Need help or have questions?',
    answer: `
      <p>Join us on Discord → <a href="https://discord.gg/dropeexyz" class="font-semibold text-white/[.87] underline" target="_blank">discord.gg/dropeexyz</a></p>
      <p class="mt-3">Before opening a ticket, we recommend introducing yourself and describing your issue in the general chat, our team and community are there to help.</p>
    `,
  },
]

const faq = computed(() => isAfterSnapshot.value ? postSnapshotFaq : preSnapshotFaq)

function onWalletConnectClick() {
  if (playerWalletAddress.value) return
  open()
}

function onReachLevelClick() {
  if (userReachedMinLevel.value) return
  router.push({ name: RouteName.MINE })
}

function getCurrentAchievementValue(categoryId: AchievementType) {
  if (!achievements.value || !(categoryId in achievements.value)) return 0
  return achievements.value[categoryId]
}
</script>
<template>
  <div class="flex-1 h-full w-full flex flex-col relative pb-5">
    <div class="h-full w-full flex flex-col">
      <div class="w-full relative flex items-center justify-center overflow-visible">
        <img
          class="w-[350px] -mt-[80px] -mb-[70px]"
          src="@/assets/images/periodic/tge/dropee-token.png"
        >
      </div>
      <div class="flex items-center gap-2 text-sm font-bold text-white/60 px-1">
        <span class="h-px w-4 bg-white/[.38]" />
        <span class="whitespace-nowrap uppercase">LISTING DATE</span>
        <span class="h-px flex-1 bg-white/[.38]" />
      </div>
      <template v-if="!!countdown">
        <div class="mt-8 text-center text-[20px] leading-7 font-semibold">
          Phase 1 snapshot in
        </div>
        <CountdownDisplay
          class="mt-3 w-full !gap-x-2"
          box-outer-class="shadow-[0px_0px_38.6px_0px_rgba(230,185,12,0.45)] bg-gold-gradient"
          :countdown="countdown"
        />
      </template>
      <div
        v-else
        class="relative w-full mt-4 mb-8 bg-dark-bottom-layer p-4 rounded-3xl"
      >
        <img
          class="absolute right-4 object-cover size-[72px] -top-5"
          src="@/assets/images/periodic/tge/airdrop-parachute.webp"
        >
        <div
          class="w-full rounded-xl shadow-[0px_0px_38.6px_0px_rgba(230,185,12,0.45)] border-4 border-[#FFF246] bg-dark-top-most-layer p-2 mb-5 font-bold text-[22px] leading-8 text-center"
        >
          <span class="block">TGE Date: TBA</span>
          <span class="text-sm leading-6 font-semibold text-white/60 text-center">
            <a
              href="https://t.me/dropee_community"
              class="text-green-primary underline"
              target="_blank"
            >Follow us</a> on our TG for live announcements
          </span>
        </div>
        <div v-if="isAfterSnapshot" class="flex flex-col p-4 rounded-3xl bg-dark-bottom-layer">
          <div class="flex gap-3 font-semibold text-base mb-4">
            <span class="relative">
              <span class="h-6 flex items-center">
                <span
                  class="rounded-full bg-[#999999] min-w-[14px] min-h-[14px] size-3.5 flex items-center justify-center"
                >
                  <CheckCircleThin class="size-2.5 text-green-primary" />
                </span>
              </span>
              <span class="absolute top-3.5 left-1/2 -translate-x-1/2 h-14 border-l border-dashed border-[#999999]" />
            </span>
            <p>
              <span class="block uppercase text-white/[.87]">Phase 1</span>
              <span class="text-sm font-medium text-white/[.60]">ended 31 May 2025</span>
            </p>
            <p class="ml-auto flex gap-1">
              <span class="text-white/[.87]">Snapshot</span>
              <span class="size-6 flex items-center justify-center">
                <CheckCircleThin class="size-6 text-green-primary" />
              </span>
            </p>
          </div>
          <div class="flex gap-3 font-semibold text-base">
            <span class="relative">
              <span class="h-6 flex items-center">
                <span
                  class="rounded-full bg-[#999999] min-w-[14px] min-h-[14px] size-3.5 flex items-center justify-center"
                >
                  <CheckCircleThin class="size-2.5 text-green-primary" />
                </span>
              </span>
            </span>
            <p>
              <span class="block uppercase text-white/[.87]">Phase 2</span>
              <span class="text-sm font-medium text-white/[.60]">ended 19 Dec 2025</span>
            </p>
            <p class="ml-auto flex gap-1">
              <span class="text-white/[.87]">Snapshot</span>
              <span class="size-6 flex items-center justify-center">
                <CheckCircleThin class="size-6 text-green-primary" />
              </span>
            </p>
          </div>
          <div class="mt-3">
            <p class="flex justify-between text-lg font-bold pl-[26px]">
              <span class="block uppercase text-white/[.87]">TOTAL</span>
              <span class="text-green-primary">Calculating...</span>
            </p>
          </div>
        </div>
        <div v-else class="flex flex-col gap-4">
          <div class="flex gap-3 font-semibold text-base">
            <span class="relative">
              <span class="h-6 flex items-center">
                <span
                  class="rounded-full bg-[#999999] min-w-[14px] min-h-[14px] size-3.5 flex items-center justify-center"
                >
                  <CheckCircleThin class="size-2.5 text-green-primary" />
                </span>
              </span>
              <span class="absolute top-3.5 left-1/2 -translate-x-1/2 h-14 border-l border-dashed border-[#999999]" />
            </span>
            <p>
              <span class="block uppercase text-white/[.87]">Phase 1</span>
              <span class="text-sm font-medium text-white/[.60]">Allocation in progress</span>
            </p>
            <p class="ml-auto flex gap-1">
              <span class="text-white/[.87]">Snapshot</span>
              <span class="size-6 flex items-center justify-center">
                <CheckCircleThin class="size-6 text-green-primary" />
              </span>
            </p>
          </div>
          <div class="flex gap-3 font-semibold text-base">
            <span class="h-6 flex items-center">
              <span
                class="rounded-full bg-[#999999]/[.22] border-2 border-[#999999] min-w-[14px] min-h-[14px] size-3.5"
              />
            </span>
            <p>
              <span class="block uppercase text-white/[.87]">Phase 2</span>
              <span class="text-sm font-medium text-white/[.60]">Keep playing, earn more!</span>
            </p>
            <p class="ml-auto flex gap-1">
              <span class="text-white/[.87]">Live</span>
              <span class="size-6 flex items-center justify-center">
                <span class="size-3.5 rounded-full min-w-[14px] bg-green-primary pulse-circle" />
              </span>
            </p>
          </div>
        </div>
      </div>
      <!-- presale link -->
      <BaseButton
        v-if="isAfterSnapshot"
        style-type="gold"
        class="mb-8"
        url="https://dlp.tropee.com/"
        disabled
      >
        Go to $DROPEE token app
      </BaseButton>
      <div class="flex items-center gap-2 text-sm font-bold text-white/60 px-1">
        <span class="h-px w-4 bg-white/[.38]" />
        <span class="whitespace-nowrap uppercase">YOUR WALLET</span>
        <span class="h-px flex-1 bg-white/[.38]" />
      </div>
      <div class="flex flex-col gap-y-3 mb-6">
        <span class="text-sm font-medium leading-5 text-white/60">This wallet will be used for the airdrop claim.</span>
        <WalletConnect :disable-disconnect="isAfterSnapshot" />
      </div>
      <div class="flex items-center gap-2 text-sm font-bold text-white/60 px-1">
        <span class="h-px w-4 bg-white/[.38]" />
        <span class="whitespace-nowrap uppercase">Airdrop allocation</span>
        <span class="h-px flex-1 bg-white/[.38]" />
      </div>
      <BaseLinkButton
        v-if="!isAfterSnapshot"
        class="mt-3 !justify-start"
        text-class="text-[18px] leading-[18px]"
        chevron-class="w-[18px] h-[18px] min-w-[18px]"
        @click="showSnapshotInfoModal = true"
      >
        How your airdrop allocation works
      </BaseLinkButton>
      <div class="mt-4 flex flex-col w-full gap-y-3">
        <div class="text-[18px] leading-6 font-bold">
          Eligibility
        </div>
        <div
          class="w-full flex items-center rounded-xl bg-dark-bottom-layer py-[14px] px-3 gap-x-3"
          @click="onWalletConnectClick"
        >
          <img
            class="size-8 min-w-[32px]"
            src="@/assets/images/gifs/money.gif"
          >
          <div class="text-base -tracking-[0.32px] font-semibold">
            Connect wallet
          </div>
          <div class="size-6 min-w-[24px] ml-auto">
            <ArrowRightIcon
              v-if="!playerWalletAddress"
              class="w-full h-full"
            />
            <CheckIconThin
              v-else
              class="w-full h-full text-green-primary"
            />
          </div>
        </div>
        <div
          class="w-full flex items-center rounded-xl bg-dark-bottom-layer py-[14px] px-3 gap-x-3"
          @click="onReachLevelClick"
        >
          <img
            class="size-8 min-w-[32px]"
            src="@/assets/images/emojis/emoji-arrow-up.png"
          >
          <div class="text-base -tracking-[0.32px] font-semibold">
            Reach level {{ minLevel }}
          </div>
          <div class="size-6 min-w-[24px] ml-auto">
            <ArrowRightIcon
              v-if="!minLevel"
              class="w-full h-full"
            />
            <CheckIconThin
              v-else
              class="w-full h-full text-green-primary"
            />
          </div>
        </div>
      </div>

      <!--			Stats:-->
      <div
        v-if="!isFetchingAchievements"
        class="mt-8 w-full flex flex-col"
      >
        <div class="text-[18px] leading-6 font-bold">
          Your Stats
        </div>
        <div
          v-for="(categoryGroup, categoryKey) in categoryGroups"
          class="mt-4 flex flex-col gap-y-3 w-full"
          :key="categoryKey"
        >
          <div class="text-base font-semibold">
            {{ categoryTitleMap[categoryKey] }}
          </div>

          <template v-if="categoryKey === AchievementCategory.DropeeJourney">
            <div class="w-full flex items-center rounded-xl bg-dark-bottom-layer py-[14px] px-3 gap-x-3">
              <img
                class="size-8 min-w-[32px] object-cover"
                src="@/assets/images/achievements/building.png"
              >
              <div class="text-left min-w-0 truncate text-base leading-5 font-semibold -tracking-[0.32px] flex-1">
                Level
              </div>
              <div class="text-base -tracking-[0.32px] font-semibold">
                {{ player.level }}
              </div>
            </div>
            <div class="w-full flex items-center rounded-xl bg-dark-bottom-layer py-[14px] px-3 gap-x-3">
              <img
                class="size-8 min-w-[32px] object-cover"
                src="@/assets/images/achievements/building.png"
              >
              <div class="text-left min-w-0 truncate text-base leading-5 font-semibold -tracking-[0.32px] flex-1">
                Rank on that level
              </div>
              <div class="text-base -tracking-[0.32px] font-semibold">
                {{ currentRank ? '#' + formatUnshortenedNumber(currentRank) : '-' }}
              </div>
            </div>
          </template>

          <div
            class="w-full flex items-center rounded-xl bg-dark-bottom-layer py-[14px] px-3 gap-x-3"
            v-for="(achievement, index) in categoryGroup"
            :key="index"
          >
            <img
              class="size-8 min-w-[32px] object-cover"
              :src="getMilestone(achievement.type, getCurrentAchievementValue(achievement.type)).icon"
            >
            <div class="text-left min-w-0 truncate text-base leading-5 font-semibold -tracking-[0.32px] flex-1">
              {{ achievement.data.title }}
            </div>
            <div class="text-base -tracking-[0.32px] font-semibold">
              {{ formatUnshortenedNumber(getCurrentAchievementValue(achievement.type)) }}
            </div>
          </div>

          <template v-if="categoryKey === AchievementCategory.DropeeJourney">
            <div class="w-full flex items-center rounded-xl bg-dark-bottom-layer py-[14px] px-3 gap-x-3">
              <img
                class="size-8 min-w-[32px] object-cover"
                src="@/assets/images/achievements/badge.png"
              >
              <div class="text-left min-w-0 truncate text-base leading-5 font-semibold -tracking-[0.32px] flex-1">
                Loyalty Leaderboard Rank
              </div>
              <div class="text-base -tracking-[0.32px] font-semibold">
                {{ effortLeaderboardCurrentRank ? '#' + formatUnshortenedNumber(effortLeaderboardCurrentRank) : '-' }}
              </div>
            </div>
          </template>
        </div>
      </div>

      <!--			FAQs:-->
      <div
        class="mt-8 w-full flex flex-col"
      >
        <div class="text-[18px] leading-6 font-bold">
          FAQs
        </div>
        <div class="mt-4 w-full flex flex-col gap-y-3">
          <FAQBox
            v-for="(faqData, index) in faq"
            :key="index"
            :question="faqData.question"
            :answer="faqData.answer"
          />
        </div>
      </div>
    </div>

    <SnapshotInfoModal
      :show="showSnapshotInfoModal"
      @hide="showSnapshotInfoModal = false"
    />
  </div>
</template>
<style scoped lang="scss">
.gold-text {
	background: linear-gradient(105deg, #FFCB00 10.46%, #B57800 61.24%, #B77C07 70.47%, #BF881B 82.01%, #CB9D3C 95.86%, #DCB96A 112.01%, #F1DDA5 130.48%, #FFF4C9 139.71%, #EACE88 153.55%, #D8AD4E 167.4%, #CB9423 178.94%, #C38509 188.17%, #C08000 192.79%, #C58500 197.41%, #EAB200 227.41%, #F9C300 241.26%);
	background-clip: text;
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
}
.gold-text2 {
	background: linear-gradient(
			to right,
			#fff246 0%,
			#e5b80b 37%,
			#fff571 69%,
			#e5b80b 100%
	);
	background-clip: text;
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
}

.bg-gold-gradient {
	background-image: linear-gradient(
			to right,
			#fff246 0%,
			#e5b80b 37%,
			#fff571 69%,
			#e5b80b 100%
	);
}

@keyframes pulse-circle {
	0% {
		filter: drop-shadow(0px 0px 0px rgba(187, 238, 83, 0)); /* No shadow */
	}
	50% {
		filter: drop-shadow(0px 0px 4px #3B9A7C);
	}
	100% {
		filter: drop-shadow(0px 0px 0px rgba(187, 238, 83, 0));
	}
}

.pulse-circle {
	animation: pulse-circle 1.5s infinite linear;
	will-change: filter;
}
</style>
